import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronLeft, 
  ChevronRight, 
  Edit, 
  Eye, 
  Plus,
  MapPin,
  Phone,
  Globe,
  Trash,
  AlertCircle,
  Store,
  Loader2,
  Building2
} from 'lucide-react';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { HelpIcon } from '../components/HelpIcon';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { useToast } from '../components/Toast';
import { useRbac } from '../contexts/RbacContext';
import { apiClient } from '../utils/api';
import { formatOpeningPeriods } from '../utils/openingHours';
import { RootState } from '../store';

interface Shop {
  id: string;
  name: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  phone: string;
  email: string;
  website?: string;
  socialMedia: {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
  };
  openingHours: {
    monday?: { open: string; close: string };
    tuesday?: { open: string; close: string };
    wednesday?: { open: string; close: string };
    thursday?: { open: string; close: string };
    friday?: { open: string; close: string };
    saturday?: { open: string; close: string };
    sunday?: { open: string; close: string };
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  companyId?: string;
  companyName?: string;
}

interface ShopsResponse {
  success: boolean;
  data: {
    shops: Shop[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export function ShopsListPage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { can } = useRbac();
  const canHardDelete = can('entities:delete:hard');
  const canCreateShop = can('platform:shops:create');
  
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; shopIds: string[] }>({
    isOpen: false,
    shopIds: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; shopIds: string[] }>({
    isOpen: false,
    shopIds: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);
  
  // Filters and pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  
  const limit = 20;
  const isBusy = loading || deletingId !== null || bulkDeleting || bulkHardDeleting;
  const { needsShopSelection, canLoad, currentShopId, isAllShopsMode } = useShopScopedPage();
  const role = String(useSelector((state: RootState) => state.auth.user?.role) || '')
    .toLowerCase()
    .replace(/-/g, '_');
  const isSuperAdmin = role === 'super_admin';
  const showFullCatalog = isAllShopsMode || isSuperAdmin;

  const visibleShopIds = useMemo(() => shops.map((s) => s.id), [shops]);
  const {
    selectedIds: selectedShopIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleShopIds);

  useEffect(() => {
    setPage(1);
  }, [currentShopId, isAllShopsMode]);

  useEffect(() => {
    if (!canLoad) return;
    fetchShops();
  }, [canLoad, currentShopId, showFullCatalog, search, statusFilter, companyFilter, cityFilter, sortKey, sortDir, page]);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await apiClient('/api/admin/companies?limit=500');
        if (res.ok) {
          const data = await res.json();
          setCompanies(data.data?.companies || []);
        }
      } catch {
        setCompanies([]);
      }
    };
    loadCompanies();
  }, []);

  const fetchShops = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(companyFilter && { companyId: companyFilter }),
        ...(cityFilter && { city: cityFilter }),
        ...(showFullCatalog && { allShops: 'true' }),
      });

      const response = await apiClient(`/api/admin/shops?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch shops');
      }

      const data: ShopsResponse = await response.json();
      
      if (data.success) {
        setShops(data.data.shops);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        setError(data.message || 'Failed to load shops');
      }
    } catch (error) {
      console.error('Fetch shops error:', error);
      setError('Failed to load shops');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (shopId: string) => {
    navigate(`/shops/${shopId}/edit`);
  };

  const handleView = (shopId: string) => {
    navigate(`/shops/${shopId}`);
  };

  const handleDelete = async (shopId: string, shopName: string) => {
    const confirmed = window.confirm(`Supprimer le shop "${shopName}" ? Cette action est définitive.`);
    if (!confirmed) return;

    try {
      setDeletingId(shopId);
      setError(null);
      const response = await apiClient(`/api/admin/shops/${shopId}`, { method: 'DELETE' });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        const reason = data?.message || data?.error || 'Impossible de supprimer le shop';
        setError(reason);
        showToast(reason, 'error', 7000);
        return;
      }

      await fetchShops();
      showToast('Shop supprimé avec succès', 'success');
    } catch (err) {
      console.error('Delete shop error:', err);
      setError('Impossible de supprimer le shop');
      showToast('Impossible de supprimer le shop', 'error', 7000);
    } finally {
      setDeletingId(null);
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedShopIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, shopIds: Array.from(selectedShopIds) });
  };
  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, shopIds: [] });
  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.shopIds.length === 0) return;
    try {
      setBulkDeleting(true);
      setError(null);

      const failed: Array<{ id: string; message: string }> = [];

      for (const shopId of bulkDeleteConfirm.shopIds) {
        try {
          const resp = await apiClient(`/api/admin/shops/${shopId}`, { method: 'DELETE' });
          let data: any = null;
          try {
            data = await resp.json();
          } catch {
            failed.push({ id: shopId, message: 'Réponse du serveur invalide' });
            continue;
          }
          if (!resp.ok || !data?.success) {
            failed.push({ id: shopId, message: data?.message || 'Impossible de supprimer le shop' });
          }
        } catch {
          failed.push({ id: shopId, message: 'Une erreur est survenue lors de la suppression.' });
        }
      }

      setBulkDeleteConfirm({ isOpen: false, shopIds: [] });
      clearSelection();
      await fetchShops();

      const deletedCount = bulkDeleteConfirm.shopIds.length - failed.length;
      if (failed.length === 0) {
        showToast(`${deletedCount} shop${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''} avec succès`, 'success');
      } else if (deletedCount === 0) {
        const msg = `Aucun shop supprimé. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      } else {
        const msg = `${deletedCount} shop${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''}. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedShopIds.size === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, shopIds: Array.from(selectedShopIds) });
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, shopIds: [] });
  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.shopIds.length === 0) return;
    try {
      setBulkHardDeleting(true);
      setError(null);

      const failed: Array<{ id: string; message: string }> = [];

      for (const shopId of bulkHardDeleteConfirm.shopIds) {
        try {
          const resp = await apiClient(`/api/admin/shops/${shopId}?hardDelete=true`, { method: 'DELETE' });
          let data: any = null;
          try {
            data = await resp.json();
          } catch {
            failed.push({ id: shopId, message: 'Réponse du serveur invalide' });
            continue;
          }
          if (!resp.ok || !data?.success) {
            failed.push({ id: shopId, message: data?.message || 'Impossible de supprimer le shop définitivement' });
          }
        } catch {
          failed.push({ id: shopId, message: 'Une erreur est survenue lors de la suppression définitive.' });
        }
      }

      setBulkHardDeleteConfirm({ isOpen: false, shopIds: [] });
      clearSelection();
      await fetchShops();

      const deletedCount = bulkHardDeleteConfirm.shopIds.length - failed.length;
      if (failed.length === 0) {
        showToast(`${deletedCount} shop${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''} définitivement`, 'success');
      } else if (deletedCount === 0) {
        const msg = `Aucun shop supprimé définitivement. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      } else {
        const msg = `${deletedCount} shop${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''} définitivement. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      }
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const handleCreate = () => {
    navigate('/shops/create');
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatOpeningHours = (openingHours: any) => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const label = formatOpeningPeriods(openingHours?.[today]);
    return label === 'Fermé' ? 'Fermé aujourd\'hui' : label;
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  if (loading && shops.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des shops...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      <ToastComponent />
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
                <span>Gestion des Shops</span>
                <HelpIcon 
                  docPath="04-boutiques#04-2-liste" 
                  tooltip="Documentation : Liste des boutiques"
                />
              </h1>
              <p className="text-textSecondary mt-1">
                {total} shop{total !== 1 ? 's' : ''} au total
              </p>
            </div>
          </div>
          {canCreateShop && (
            <button
              onClick={handleCreate}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter un Shop</span>
            </button>
          )}
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={openBulkDeleteConfirm}
                disabled={bulkDeleting || bulkHardDeleting || deletingId !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer les shops sélectionnés"
              >
                <Trash className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
                <button
                  onClick={openBulkHardDeleteConfirm}
                  disabled={bulkDeleting || bulkHardDeleting || deletingId !== null}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Supprimer définitivement les shops sélectionnés"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Suppression définitive ({selectedCount})</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un shop..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Company Filter */}
          <div className="relative">
            <select
              value={companyFilter}
              onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les entreprises</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* City Filter */}
          <div className="relative">
            <input
              type="text"
              placeholder="Filtrer par ville..."
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="input-field"
            />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch('');
              setStatusFilter('');
              setCompanyFilter('');
              setCityFilter('');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchShops}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Shops Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={shops.length === 0 || bulkDeleting || bulkHardDeleting || deletingId !== null}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Sélectionner tous les shops de la page"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <Store className="w-4 h-4" />
                    <span>Shop</span>
                    {sortKey === 'name' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('companyName')}
                >
                  <div className="flex items-center space-x-1">
                    <Building2 className="w-4 h-4" />
                    <span>Compagnie</span>
                    {sortKey === 'companyName' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>Adresse</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Phone className="w-4 h-4" />
                    <span>Contact</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Horaires
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('isActive')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'isActive' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Créé le</span>
                    {sortKey === 'createdAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shops.map((shop) => (
                <tr key={shop.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap align-top">
                    <input
                      type="checkbox"
                      checked={selectedShopIds.has(shop.id)}
                      disabled={bulkDeleting || bulkHardDeleting || deletingId !== null}
                      onChange={() => toggleSelect(shop.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label={`Sélectionner le shop ${shop.name}`}
                    />
                  </td>
                  <td className="px-6 py-4 align-top max-w-[220px]">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-900 break-words">
                        {shop.name}
                      </div>
                      {shop.website && (
                        <div className="text-sm text-gray-500 flex items-center break-all">
                          <Globe className="w-3 h-3 mr-1" />
                          <a href={shop.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary-600">
                            {shop.website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 align-top max-w-[220px]">
                    {shop.companyName ? (
                      <div className="flex items-start space-x-2">
                        <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="space-y-0.5">
                          <div className="text-sm font-medium text-gray-900 break-words">
                            {shop.companyName}
                          </div>
                          <div className="text-xs text-gray-500 break-words">
                            {shop.companyId}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic">
                        Aucune compagnie
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 align-top max-w-[240px] break-words">
                    <div className="text-sm text-gray-900">
                      {shop.address.street}
                    </div>
                    <div className="text-sm text-gray-500">
                      {shop.address.city}, {shop.address.postalCode}
                    </div>
                  </td>
                  <td className="px-6 py-4 align-top max-w-[200px] break-words">
                    <div className="text-sm text-gray-900">{shop.phone}</div>
                    <div className="text-sm text-gray-500">{shop.email}</div>
                  </td>
                  <td className="px-6 py-4 align-top text-sm text-gray-900 max-w-[160px] break-words">
                    {formatOpeningHours(shop.openingHours)}
                  </td>
                  <td className="px-6 py-4 align-top">
                    {getStatusBadge(shop.isActive)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 align-top">
                    {formatDate(shop.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleView(shop.id)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded"
                        title="Voir le shop"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(shop.id)}
                        className="text-primary-600 hover:text-primary-700 p-1 rounded"
                        title="Modifier le shop"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(shop.id, shop.name)}
                        className="text-red-500 hover:text-red-600 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Supprimer le shop"
                        disabled={deletingId === shop.id}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Empty State */}
        {!loading && shops.length === 0 && (
          <div className="text-center py-12">
            <Store className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun shop trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || statusFilter || companyFilter || cityFilter 
                ? 'Essayez de modifier vos filtres de recherche.'
                : canCreateShop
                  ? 'Commencez par ajouter votre premier shop.'
                  : 'Aucun shop disponible pour votre compte.'
              }
            </p>
            {canCreateShop && (
              <div className="mt-6">
                <button
                  onClick={handleCreate}
                  className="btn-primary flex items-center space-x-2 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ajouter un Shop</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(page - 1) * limit + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(page * limit, total)}</span> sur{' '}
                  <span className="font-medium">{total}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay for subsequent loads */}
      {loading && shops.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${bulkDeleting ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.shopIds.length}</strong> shop{bulkDeleteConfirm.shopIds.length !== 1 ? 's' : ''} ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Hard Delete: two-step confirmation */}
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={`shop${bulkHardDeleteConfirm.shopIds.length !== 1 ? 's' : ''}`}
        count={bulkHardDeleteConfirm.shopIds.length}
        isLoading={bulkHardDeleting}
      />
    </div>
  );
}